import React, { useContext, useEffect, useMemo } from 'react';
import { Grid } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { ListingCreationFlowContext } from '../../../context/Listing';
import { ListingImageThumbnail } from '../../../components/listing/ListingImageThumbnail';
import { useListingEditPhotos } from '../../../hooks/listing/useListingEditPhotos';
import { removeKeysFromObject } from '../../../types/utils/objectMutation';
import { Listing } from '../../../types/db';

/** The page for the pictures of a listing in the creation flow */
export const ListingPicturesPage = () => {
    return <Container />;
}

/** The container taking care for the picture of a listing in the creation flow */
const Container = () => {
    const listingCreationFlowContext = useContext(ListingCreationFlowContext);
    if (!listingCreationFlowContext) {
        throw Error('Need listingCreationFlowContext')
    }

    const { listing, setActivePageInfo } = listingCreationFlowContext;

    const { photos: files, onDropPhotos, deletePhoto, onDragFileStart, onDragFileEnd, onDragFileDrop, onDragFileOver, onDrageStyleFn }
        = useListingEditPhotos(listing.id || '', listing.photos_ordered as Listing['photos_ordered']);

    useEffect(() => {
        if (files.length > 0 && files.every(file => file.percent === 100 && !!file.path && !!file.id)) {
            setActivePageInfo({
                photos_ordered: files.map(file => ({ ...removeKeysFromObject(file, ['preview', "percent", "error", 'description']) }))
            }, true);
        } else {
            setActivePageInfo({}, false);
        }
    }, [files, setActivePageInfo]);


    const {
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject } = useDropzone({
            noDrag: false,
            accept: {
                'image/*': ['.jpeg', '.png']
            },
            onDrop: onDropPhotos,
        });

    const style = useMemo(() => ({
        ...{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            padding: '20px',
            borderWidth: 2,
            borderRadius: 2,
            borderColor: '#eeeeee',
            borderStyle: 'dashed',
            backgroundColor: '#fafafa',
            color: '#bdbdbd',
            outline: 'none',
            transition: 'border .24s ease-in-out'
        },
        ...(isFocused ? { borderColor: '#2196f3' } : {}),
        ...(isDragAccept ? { borderColor: '#00e676' } : {}),
        ...(isDragReject ? { borderColor: '#ff1744' } : {})
    }), [
        isFocused,
        isDragAccept,
        isDragReject
    ]);

    return (
        <section>
            <div {...getRootProps({ style })}>
                <input {...getInputProps()} />
                <p>Drag 'n' drop some files here, or click to select files</p>
            </div>
            {/* Note: find a better way to fit height properly than rowSpacing */}
            <Grid container spacing={2} rowSpacing={4} my={2} justifyContent={'center'} alignItems={'stretch'}>
                {
                    files.map((file, index) =>
                        <Grid item key={`${file.id}-${file.path}-${file.name}`} xs={12} sm={6} md={3}
                            draggable
                            onDragStart={onDragFileStart(index)}
                            onDragOver={onDragFileOver}
                            onDrop={onDragFileDrop(index)}
                            onDragEnd={onDragFileEnd}
                            style={onDrageStyleFn(index)}>
                            <ListingImageThumbnail
                                name={file.name}
                                description={file.description}
                                url={file.preview}
                                percent={file.percent}
                                error={file.error}
                                onDeleteClick={file.percent === 100 ? () => deletePhoto(file) : undefined}
                            />
                        </Grid>
                    )
                }
            </Grid>
        </section>
    )
}
